import { useEffect, type ReactNode } from "react";
import { useSidebarDrawerStore } from "@/stores/sidebarDrawerStore";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/button";
import { Tooltip } from "@/shared/components/misc";

export interface SidebarNavItem {
  path: string;
  label: string;
  icon: string;
  end?: boolean;
  /** Count pill on the right of the item (e.g. items awaiting input). Hidden when absent or 0. */
  badge?: number;
  /** Overrides NavLink's own matching — for an item whose sub-pages aren't all under its path. */
  isActive?: (pathname: string) => boolean;
}

export interface SidebarNavSection {
  title: string;
  items: SidebarNavItem[];
}

interface PortalSidebarProps {
  logoTitle: ReactNode;
  logoSubtitle: string;
  profileSlot?: ReactNode;
  navSections: SidebarNavSection[];
  onSettingsClick?: () => void;
  onSignOut: () => void;
}

const NAV_LINK_CLASS = cn(
  "group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium",
  "text-[#3A3D42] transition-[background-color] duration-200 ease-out motion-reduce:transition-none",
  "hover:bg-[#F5F5F5]",
  "aria-[current=page]:bg-[#E4002B80] aria-[current=page]:text-white",
);

function NavSection({ title, items }: SidebarNavSection) {
  const { pathname } = useLocation();
  return (
    <>
      <p className="mb-1.5 mt-8 px-3 text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] first:mt-0">
        {title}
      </p>
      <div className="space-y-0.5">
        {items.map((item) => {
          const content = (
            <>
              <Icon
                icon={item.icon}
                className="h-4 w-4 shrink-0 text-[#111827] group-aria-[current=page]:text-white"
              />
              {item.label}
              {item.badge ? (
                <span
                  aria-label={`${item.badge} awaiting your input`}
                  className="ml-auto flex min-w-5 items-center justify-center rounded-full bg-[#E4002B]/50 px-1.5 text-[11px] font-bold leading-5 text-white group-aria-[current=page]:bg-white group-aria-[current=page]:text-[#E4002B]"
                >
                  {item.badge}
                </span>
              ) : null}
            </>
          );
          // A custom matcher: a plain Link with aria-current set here (NavLink
          // only ever marks its own path match as current).
          if (item.isActive) {
            return (
              <Link
                key={item.path}
                to={item.path}
                viewTransition
                aria-current={item.isActive(pathname) ? "page" : undefined}
                className={NAV_LINK_CLASS}
              >
                {content}
              </Link>
            );
          }
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              // Cross-fades the routed <main> content on navigation via the View
              // Transitions API (scoped in index.css so only <main> animates).
              viewTransition
              // State is driven off the `aria-current="page"` attribute NavLink sets
              // on the active link, not a render-prop, so the icon/label stay mounted
              // across a navigation and only their styles change. Only background-color
              // is transitioned — text/icon color snaps — so the incoming link never
              // flashes its dark inactive text while the red highlight fades in.
              className={NAV_LINK_CLASS}
            >
              {content}
            </NavLink>
          );
        })}
      </div>
    </>
  );
}

export function PortalSidebar({
  logoTitle,
  logoSubtitle,
  profileSlot,
  navSections,
  onSettingsClick,
  onSignOut,
}: PortalSidebarProps) {
  const { pathname } = useLocation();
  const open = useSidebarDrawerStore((s) => s.open);
  const setOpen = useSidebarDrawerStore((s) => s.setOpen);

  // Drawer mode (< lg): picking a page closes it, and so does Escape.
  useEffect(() => setOpen(false), [pathname, setOpen]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  return (
    <>
      {open && (
        <div
          aria-hidden
          data-testid="sidebar-backdrop"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 bg-[#0A2947]/40 lg:hidden"
        />
      )}
    <aside
      id="portal-sidebar"
      className={cn(
        // Rounded right corners, no border line. A very light shadow outlines the curve
        // against the grey page (the old dark 0.12 one smudged where it met the header).
        "flex h-screen w-64 shrink-0 flex-col rounded-tr-3xl rounded-br-3xl bg-white shadow-[2px_0_12px_rgba(10,41,71,0.04)]",
        // < lg: off-canvas drawer over the page; lg+: always in the layout.
        "fixed inset-y-0 left-0 z-50 transition-transform duration-200 ease-out motion-reduce:transition-none lg:relative lg:z-10 lg:translate-x-0",
        // Only the open drawer (below lg, floating over the page) gets a soft shadow.
        open ? "translate-x-0 max-lg:shadow-xl" : "-translate-x-full",
      )}
    >
      <div className="flex h-[73px] items-center gap-3 border-b border-[#E5E7EB] px-5">
        <img src="/logo.png" alt="Logo" className="h-10 w-10 rounded-lg object-contain" />
        <div className="flex flex-col gap-0">
          <h1 className="text-lg leading-tight tracking-tight text-[#111827]" style={{ fontWeight: 900, letterSpacing: "-0.02em" }}>
            {logoTitle}
          </h1>
          <p className="text-[11px] font-medium leading-tight text-[#4B5563]">{logoSubtitle}</p>
        </div>
      </div>

      {profileSlot && <div className="border-b border-[#E5E7EB]">{profileSlot}</div>}

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        {navSections.map((section) => (
          <NavSection key={section.title} title={section.title} items={section.items} />
        ))}
      </nav>

      <div className="shrink-0 border-t border-[#E5E7EB] p-2">
        <div className="flex items-center gap-1">
          <Tooltip title="Settings" placement="top">
            <Button
              variant="ghost"
              size="icon"
              className="flex items-center justify-center hover:!bg-[#F5F5F5]"
              onClick={onSettingsClick}
            >
              <Icon icon="lucide:settings" className="h-4 w-4 text-[#111827]" />
            </Button>
          </Tooltip>
          <Tooltip title="Logout" placement="top">
            <Button
              variant="ghost"
              size="icon"
              className="flex items-center justify-center hover:!bg-[#F5F5F5]"
              onClick={onSignOut}
            >
              <Icon icon="lucide:log-out" className="h-4 w-4 text-[#111827]" />
            </Button>
          </Tooltip>
        </div>
      </div>
    </aside>
    </>
  );
}

/** Header button that opens the sidebar drawer — only shown below `lg`. */
export function SidebarToggle() {
  const open = useSidebarDrawerStore((s) => s.open);
  const setOpen = useSidebarDrawerStore((s) => s.setOpen);
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Open menu"
      aria-controls="portal-sidebar"
      aria-expanded={open}
      onClick={() => setOpen(!open)}
      className="!flex shrink-0 items-center justify-center hover:!bg-[#EDEDED] lg:!hidden"
    >
      <Icon icon="lucide:menu" className="h-5 w-5 text-[#111827]" />
    </Button>
  );
}
